import { createContext, useContext, useMemo, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

const DatabaseContext = createContext({
  databaseId: null,
  schema: null,
  databases: [],
  isLoading: true,
  setDatabase: () => {},
});

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

export function DatabaseProvider({ children }) {
  const [selectedSlug, setSelectedSlug] = useState(null);

  // Fetch all databases
  const { data: databases = [], isLoading } = useQuery({
    queryKey: ['databases'],
    queryFn: async () => {
      const response = await api.get('/databases');
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

  // Determine current database: explicit selection → default → first
  const currentDb = useMemo(() => {
    if (databases.length === 0) return null;
    if (selectedSlug) {
      const bySlug = databases.find((d) => d.slug === selectedSlug);
      if (bySlug) return bySlug;
      const byId = databases.find((d) => d.id === selectedSlug);
      if (byId) return byId;
    }
    return databases.find((d) => d.isDefault) || databases[0];
  }, [databases, selectedSlug]);

  // Fetch full schema for the current database
  const { data: schema } = useQuery({
    queryKey: ['databases', currentDb?.id],
    queryFn: async () => {
      const response = await api.get(`/databases/${currentDb.id}`);
      return response.data;
    },
    enabled: !!currentDb?.id,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

  const setDatabase = useCallback((slugOrId) => {
    setSelectedSlug(slugOrId || null);
  }, []);

  const value = useMemo(() => ({
    databaseId: currentDb?.id || null,
    databaseSlug: currentDb?.slug || null,
    schema,
    databases,
    isLoading,
    setDatabase,
  }), [currentDb, schema, databases, isLoading, setDatabase]);

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
}
